import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  CheckCircle2, XCircle, MapPin, Phone, User, Clock, Camera,
  Loader2, Inbox, Filter, ChevronDown, ExternalLink, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SOURCE_COLORS = {
  'Trasa': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Polecenie': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Networking': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Przetarg': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Internet': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Inne': 'bg-slate-500/20 text-[var(--crm-text-secondary)] border-slate-500/30',
};

const STATUS_CONFIG = {
  pending: { label: 'Oczekuje', bg: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  accepted: { label: 'Przyjęty', bg: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  rejected: { label: 'Odrzucony', bg: 'bg-red-500/20 text-red-400 border-red-500/30' },
};

const BUILD_STAGE_BADGE = {
  'SSO': { label: 'SSO', cls: 'bg-orange-500/20 text-orange-500 dark:text-orange-400 border-orange-500/30' },
  'SSZ': { label: 'SSZ', cls: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  'Inny': { label: 'Inny', cls: 'bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-secondary)] border-[var(--crm-border)]' },
};

const SubmissionCard = ({ sub, onAccept, onReject }) => {
  const gps = sub.location_gps;
  const hasPhotos = sub.photos?.length > 0;
  const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;

  return (
    <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)] overflow-hidden" data-testid={`submission-card-${sub.id}`}>
      {/* Header */}
      <div className="p-4 pb-3 border-b border-[var(--crm-border)]/50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-[var(--crm-text-primary)] font-semibold text-lg truncate" data-testid="submission-client-name">
              {sub.klient?.trim() || (sub.adres_inwestycji ? `Lead z lokalizacji` : 'Lead (lokalizacja GPS)')}
            </h3>
            {!sub.klient?.trim() && sub.adres_inwestycji && (
              <p className="text-[var(--crm-text-secondary)] text-sm truncate mt-0.5">{sub.adres_inwestycji}</p>
            )}
            {sub.telefon_klienta && (
              <a href={`tel:${sub.telefon_klienta}`} className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm mt-0.5">
                <Phone className="w-3.5 h-3.5" />{sub.telefon_klienta}
              </a>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <Badge className={`text-xs border ${cfg.bg}`}>{cfg.label}</Badge>
            {sub.build_stage && BUILD_STAGE_BADGE[sub.build_stage] && (
              <Badge className={`text-xs border font-bold ${BUILD_STAGE_BADGE[sub.build_stage].cls}`} data-testid="build-stage-badge">
                {BUILD_STAGE_BADGE[sub.build_stage].label}
              </Badge>
            )}
            <Badge className={`text-xs border ${SOURCE_COLORS[sub.source] || SOURCE_COLORS['Inne']}`}>
              {sub.source}
            </Badge>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Address */}
        {sub.adres_inwestycji && (
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-4 h-4 text-[#FF6B35] mt-0.5 shrink-0" />
            <span className="text-[var(--crm-text-secondary)]">{sub.adres_inwestycji}</span>
          </div>
        )}

        {/* Map preview */}
        {gps?.lat && gps?.lng && (
          <div className="rounded-lg overflow-hidden border border-[var(--crm-border)]" data-testid="submission-map">
            <iframe
              title="Lokalizacja"
              width="100%"
              height="140"
              style={{ border: 0 }}
              loading="lazy"
              src={`https://maps.google.com/maps?q=${gps.lat},${gps.lng}&z=16&output=embed`}
            />
          </div>
        )}

        {/* Photos */}
        {hasPhotos && (
          <div data-testid="submission-photos">
            <div className="flex items-center gap-1.5 text-[var(--crm-text-secondary)] text-xs mb-1.5">
              <Camera className="w-3.5 h-3.5" />{sub.photos.length} zdjęć z terenu
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {sub.photos.slice(0, 6).map((p) => (
                <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer"
                   className="aspect-square rounded-md overflow-hidden border border-[var(--crm-border)] hover:border-[#FF6B35] transition-colors">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                    alt={p.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Build comment */}
        {sub.build_comment && (
          <div className="flex items-start gap-2 text-sm">
            <FileText className="w-4 h-4 text-[var(--crm-text-muted)] mt-0.5 shrink-0" />
            <p className="text-[var(--crm-text-secondary)] italic">{sub.build_comment}</p>
          </div>
        )}

        {/* Notes */}
        {sub.notatki_terenowe && (
          <div className="flex items-start gap-2 text-sm">
            <FileText className="w-4 h-4 text-[var(--crm-text-muted)] mt-0.5 shrink-0" />
            <p className="text-[var(--crm-text-secondary)] italic">{sub.notatki_terenowe}</p>
          </div>
        )}

        {/* Meta: who & when */}
        <div className="flex items-center justify-between text-xs text-[var(--crm-text-muted)] pt-2 border-t border-[var(--crm-border)]/40">
          <div className="flex items-center gap-1.5">
            <User className="w-3 h-3" />
            <span>{sub.submitted_by_initials || sub.submitted_by_email}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            <span>{new Date(sub.submitted_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}</span>
          </div>
        </div>

        {/* Rejection info */}
        {sub.status === 'rejected' && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm">
            <p className="text-red-400 font-medium">Odrzucony przez {sub.rejected_by_initials}</p>
            {sub.rejection_reason && <p className="text-[var(--crm-text-secondary)] mt-1">{sub.rejection_reason}</p>}
            <p className="text-[var(--crm-text-muted)] text-xs mt-1">
              {sub.rejected_at && new Date(sub.rejected_at).toLocaleString('pl-PL')}
            </p>
          </div>
        )}

        {/* Accepted info */}
        {sub.status === 'accepted' && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-sm">
            <p className="text-emerald-400 font-medium">Przyjęty przez {sub.accepted_by_initials}</p>
            <p className="text-[var(--crm-text-muted)] text-xs mt-1">
              {sub.accepted_at && new Date(sub.accepted_at).toLocaleString('pl-PL')}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons (only for pending) */}
      {sub.status === 'pending' && (
        <div className="p-4 pt-0 flex gap-2">
          <Button onClick={() => onAccept(sub)} data-testid={`accept-btn-${sub.id}`}
            className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
            <CheckCircle2 className="w-4 h-4 mr-2" />Przyjmij i utwórz projekt
          </Button>
          <Button onClick={() => onReject(sub)} data-testid={`reject-btn-${sub.id}`}
            className="h-11 px-4 bg-transparent border border-red-500/40 text-red-400 hover:bg-red-500/10">
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      )}
    </Card>
  );
};

export const LeadInbox = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [stageFilter, setStageFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);

  // Reject dialog
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = { status: filter };
      if (stageFilter !== 'all') params.build_stage = stageFilter;
      const res = await axios.get(`${API_URL}/lead-inbox`, { params });
      setSubmissions(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Brak dostępu do skrzynki');
      }
    } finally { setLoading(false); }
  }, [filter, stageFilter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleAccept = async (sub) => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/lead-inbox/${sub.id}/accept`);
      toast.success(`Lead "${sub.klient}" przyjęty!`);
      setSubmissions(prev => prev.filter(s => s.id !== sub.id));
      // Offer to navigate to the new project
      const pid = res.data.project_id;
      if (pid) {
        toast(`Otwórz projekt →`, {
          action: { label: 'Otwórz', onClick: () => navigate(`/projects/${pid}`) },
          duration: 6000,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd przyjmowania leada');
    } finally { setActionLoading(false); }
  };

  const openReject = (sub) => {
    setRejectTarget(sub);
    setRejectReason('');
    setRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/lead-inbox/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success(`Lead "${rejectTarget.klient}" odrzucony`);
      setSubmissions(prev => prev.filter(s => s.id !== rejectTarget.id));
      setRejectDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd odrzucania');
    } finally { setActionLoading(false); }
  };

  const pendingCount = submissions.filter(s => s.status === 'pending').length;
  const FILTER_OPTS = [
    { value: 'pending', label: 'Oczekujące', count: filter === 'pending' ? submissions.length : null },
    { value: 'accepted', label: 'Przyjęte' },
    { value: 'rejected', label: 'Odrzucone' },
    { value: 'all', label: 'Wszystkie' },
  ];

  return (
    <Layout>
      <div className="p-4 md:p-8 lg:p-12 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Inbox className="w-7 h-7 text-[#FF6B35]" />
            <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">
              Skrzynka leadów
            </h1>
            {filter === 'pending' && submissions.length > 0 && (
              <Badge className="bg-red-500 text-white text-sm px-2.5 py-0.5" data-testid="inbox-pending-count">
                {submissions.length}
              </Badge>
            )}
          </div>
          <p className="text-[var(--crm-text-secondary)] mt-1 text-sm">Leady przesłane przez handlowców w terenie — do akceptacji lub odrzucenia</p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {FILTER_OPTS.map(opt => (
            <Button key={opt.value}
              onClick={() => { setFilter(opt.value); setLoading(true); }}
              data-testid={`inbox-filter-${opt.value}`}
              className={`h-9 px-4 text-sm rounded-full transition-all ${
                filter === opt.value
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-[var(--crm-bg-dialog)] text-[var(--crm-text-secondary)] border border-[var(--crm-border)] hover:border-[var(--crm-text-muted)]'
              }`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        {/* Build stage filter */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-[var(--crm-text-muted)] text-xs mr-1">Etap:</span>
          {[
            { value: 'all', label: 'Wszystkie' },
            { value: 'SSO', label: 'SSO' },
            { value: 'SSZ', label: 'SSZ' },
            { value: 'Inny', label: 'Inny' },
          ].map(opt => (
            <Button key={opt.value}
              onClick={() => { setStageFilter(opt.value); setLoading(true); }}
              data-testid={`stage-filter-${opt.value}`}
              className={`h-7 px-3 text-xs rounded-full transition-all ${
                stageFilter === opt.value
                  ? opt.value === 'SSO' ? 'bg-orange-500 text-white'
                  : opt.value === 'SSZ' ? 'bg-emerald-500 text-white'
                  : 'bg-[#FF6B35] text-white'
                  : 'bg-[var(--crm-bg-dialog)] text-[var(--crm-text-muted)] border border-[var(--crm-border)] hover:border-[var(--crm-text-muted)]'
              }`}
            >
              {opt.label}
            </Button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-20">
            <Inbox className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-[var(--crm-text-secondary)] text-lg">
              {filter === 'pending' ? 'Brak oczekujących leadów' : 'Brak leadów w tej kategorii'}
            </p>
            <p className="text-[var(--crm-text-muted)] text-sm mt-1">Leady od feederów pojawią się tutaj automatycznie</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="submissions-grid">
            {submissions.map(sub => (
              <SubmissionCard key={sub.id} sub={sub} onAccept={handleAccept} onReject={openReject} />
            ))}
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[var(--crm-text-primary)] font-manrope">Odrzuć lead: {rejectTarget?.klient}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Powód odrzucenia (opcjonalnie)</Label>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                data-testid="reject-reason-input"
                placeholder="np. duplikat, poza terytorium, brak potencjału..."
                rows={3}
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] mt-1" />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setRejectDialog(false)}
                className="flex-1 bg-transparent border border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)]">
                Anuluj
              </Button>
              <Button onClick={confirmReject} disabled={actionLoading}
                data-testid="confirm-reject-button"
                className="flex-1 bg-red-600 hover:bg-red-500 text-white">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Odrzuć lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
